import React, { useEffect } from 'react';
import { useFormik, Formik } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { FieldInput, FieldDate } from '../../fields';
import FieldFile from '../../fieldFile';
import { Error } from '../../../design/elements';
const DiscountApprovalForm = ({ discountApprovalRef, defaultValues, isDiscountPrice, dueMonthly, orderData }) => {
  const validationSchema = Yup.object().shape({
    discountValue: Yup.number()     
    .transform((value, originalValue) => originalValue.trim() === '' ? null : value)     
    .nullable()     
    .moreThan(0, 'Discount value must be greater than 0')     
    .max(dueMonthly, `Discount value must be less than or equal to Due Monthly`),
    approverName: Yup.string().when('discountValue', {
      is: (discountValue) => discountValue > 0,
      then: Yup.string().required('Approver is required'),
      otherwise: Yup.string().notRequired()
    }),
    approverEmail: Yup.string().when('discountValue', {
      is: (discountValue) => discountValue > 0,
      then: Yup.string()
        .required('Approver Email is required')
        .email('Invalid email'),
      otherwise: Yup.string().notRequired()
    }),
    dateApproved: Yup.string().when('discountValue', {
      is: (discountValue) => discountValue > 0,
      then: Yup.string().required('Date Approved is required'),
      otherwise: Yup.string().notRequired()
    }),
    attachmentFile: Yup.string().when('discountValue', {
      is: (discountValue) => discountValue > 0,
      then: Yup.string().test(
        'attachmentFile',
        'Attachment file is required',
        function(value) {
          const { path, createError } = this;
          return orderData.attachments.length > 0 || !!value || createError({ path, message: 'Attachment file is required' });
        }
      ),
      otherwise: Yup.string().notRequired()
    })
  });
  const formik = useFormik({
    initialValues: defaultValues,
    onSubmit: async (values, actions) => {
      try {
        await validationSchema.validate(values, { abortEarly: false });
        if (values.discountValue) {
          values.discountValue = Number(values.discountValue).toFixed(2);
          values.discountValue = values.discountValue.toString();
        }
      } catch (validationErrors) {
        console.log(validationErrors)
        const errors = {};
        validationErrors.inner.forEach(error => {
          errors[error.path] = error.message;
        });
        actions.setErrors(errors);
      } finally {
        actions.setSubmitting(false);
      }
    }
    // validationSchema
  });
  useEffect(() => {
    if (discountApprovalRef) {
      // eslint-disable-next-line no-param-reassign
      discountApprovalRef.current = formik;
    }
  }, [discountApprovalRef, formik]);
  return (
    <Formik initialValues={formik.initialValues}>
      <form>
        <div>
          <FieldInput
            data={{
              ref: null,
              name: 'discountValue',
              type: 'Number',
              label: 'Approved Monthly Discount ($)',
              value: formik.values.discountValue,
              required: false,
              onChange: e => {
                isDiscountPrice(e.target.value)
                formik.setFieldValue('discountValue', e.target.value)
              },
              readonly: false,
              tooltip: '',
              note: null,
              disabled: false,
              noteAppearsBelowField: false,
              isShowNoteBackground: false
            }}
          />
            {formik.touched.discountValue && formik.errors.discountValue ? (
              <Error text={formik.errors.discountValue} />
            ) : null}
        </div>
        {formik.values.discountValue > 0 && (
          <div style={{marginBottom: "170px"}}>
            <FieldInput
              data={{
                ref: null,
                name: 'approverName',
                type: 'text',
                label: 'Approver Name',
                value: formik.values.approverName,
                required: true,
                onChange: e =>
                  formik.setFieldValue('approverName', e.target.value),
                readonly: false,
                tooltip: '',
                note: null,
                disabled: false,
                noteAppearsBelowField: false,
                isShowNoteBackground: false
              }}
            />
            {formik.touched.approverName && formik.errors.approverName ? (
              <Error text={formik.errors.approverName} />
            ) : null}
            <FieldInput
              data={{
                ref: null,
                name: 'approverEmail',
                type: 'email',
                label: 'Approver Email',
                value: formik.values.approverEmail,
                required: true,
                onChange: e =>
                  formik.setFieldValue('approverEmail', e.target.value),
                readonly: false,
                tooltip: '',
                note: null,
                disabled: false,
                noteAppearsBelowField: false,
                isShowNoteBackground: false
              }}
            />
            {formik.touched.approverEmail && formik.errors.approverEmail ? (
              <Error text={formik.errors.approverEmail} />
            ) : null}
            <FieldDate
              data={{
                ref: null,
                name: 'dateApproved',
                type: 'date',
                label: 'Date Approved',
                value: formik.values.dateApproved,
                required: true,
                onChange: e =>
                  formik.setFieldValue('dateApproved', e.target.value),
                readonly: false,
                tooltip: '',
                note: null,
                disabled: false,
                noteAppearsBelowField: false,
                isShowNoteBackground: false,
              }}
            />
            {formik.touched.dateApproved && formik.errors.dateApproved ? (
              <Error text={formik.errors.dateApproved} />
            ) : null}
            <FieldFile
              data={{
                ref: null,
                name: 'attachmentFile',
                label: 'Attachment',
                required: true,
                onChange: e =>{
                  formik.setFieldValue('attachmentFile',[{discountFile:e.target.files[0]}])},
                note: null,
                value: formik.values.attachmentFile,
                noteAppearsBelowField: false,
                isShowNoteBackground: false
              }}
            />
            {formik.touched.attachmentFile && formik.errors.attachmentFile ? (
              <Error text={formik.errors.attachmentFile} />
            ) : null}
          </div>
        )}
      </form>
    </Formik>
  );
};
DiscountApprovalForm.propTypes = {
  discountApprovalRef: PropTypes.shape({
    current: PropTypes.shape({}).isRequired
  }).isRequired,
  defaultValues: PropTypes.shape({
    discountValue: PropTypes.string.isRequired,
    approverEmail: PropTypes.string.isRequired,
    approverName: PropTypes.string.isRequired,
    dateApproved: PropTypes.string.isRequired,
    attachmentFile: PropTypes.string.isRequired
  }).isRequired
};
export default DiscountApprovalForm;